import { ButtonType,ButtonWidget } from '@tableau/widgets';
import React from 'react';

import { requestSpaceContents } from '~src/app/actions/spaces/RequestSpaceContentsActions';
import { getAppStore } from '~src/app/AppStore';
import { SessionServerSelectors, SessionUserSelectors } from '~src/app/selectors/SessionInfoSelectors';
import { Messages } from '~src/codegen/Localize';
import { IError } from '~src/httpClient/serverApi/types/IError';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { cssClass } from '~src/style';
import { ServerFilterClauses } from '~src/ts/helpers/ServerFilterClauses';
import { SliceableHelper } from '~src/ts/helpers/SliceableHelper';
import { FlowOutputStepInfo } from '~src/ts/models/FlowOutputStepInfo';
import { RunFlowTaskInfo } from '~src/ts/models/RunFlowTaskInfo';
import { OutputFileFormat,SubscriptionFlow, SubscriptionFlowDetails,SubscriptionFlowMetadata } from '~src/ts/ServerApi/types/SubscriptionFlow';
import { ToasterService, ToastType } from '~src/ts/services/ToasterService';

import { Flows } from '../resources/Flows';
import { RunFlowTasks } from '../resources/RunFlowTasks';
import { Recipient } from '../types/Recipient';
import { DEFAULT_SHOULD_ATTACH_OUTPUT_DATA_TO_EMAIL,DEFAULT_SHOULD_INCLUDE_LINK_TO_DATASOURCE, DEFAULT_SHOULD_SHOW_FIRST_N_ROWS, getEmailRecipientGroupIds,getEmailRecipientUserIds, isSelectedOutputAPublishedDatasource, isValidEmailTemplate } from '../utils/FlowSubscriptionUtil';
import { FlowSubscriptionDialogContent } from './FlowSubscriptionDialogContent';
import { FlowSubscriptionRunFlowTaskDropdown } from './FlowSubscriptionRunFlowTaskDropdown';

const rootClass: string = 'FlowSubscriptionCreationDialogContent';

const taskDropdownWrapper = cssClass(rootClass, 'taskDropdownWrapper', {
  paddingBottom: '12px',
});

const buttonsWrapper = cssClass(rootClass, 'buttonsWrapper', {
  display: 'flex',
  justifyContent: 'flex-end',
  paddingTop: '18px',
});

export type FlowSubscriptionCreationDialogContentProps = {
  flowId: string;
  flowName: string;
  dismiss: () => void;
}

export function FlowSubscriptionCreationDialogContent(props: FlowSubscriptionCreationDialogContentProps): JSX.Element {
  const [shouldSendToMe, setShouldSendToMe] = React.useState<boolean>(false);
  const [shouldIncludeOutputData, setShouldIncludeOutputData] = React.useState<boolean>(false);
  const [shouldShowFirstNRows, setShouldShowFirstNRows] = React.useState<boolean>(false);
  const [shouldAttachOutputDataToEmail, setShouldAttachOutputDataToEmail] = React.useState<boolean>(false);
  const [shouldIncludeLinkToDatasource, setShouldIncludeLinkToDatasource] = React.useState<boolean>(false);
  const [isFetchingRunFlowTasks, setIsFetchingRunFlowTasks] = React.useState<boolean>(true);
  const [isWaitingForCreate, setIsWaitingForCreate] = React.useState<boolean>(false);

  const [subjectValue, setSubjectValue] =
    React.useState<string>(Messages.flowSubscriptionsSubjectDefault({FLOW_NAME: props.flowName}));
  const [messageValue, setMessageValue] = React.useState<string>('');

  const [outputs, setOutputs] = React.useState<ReadonlyArray<FlowOutputStepInfo>>([]);
  const [runFlowTasks, setRunFlowTasks] = React.useState<RunFlowTaskInfo[]>([]);

  const [outputFileFormat, setOutputFileFormat] = React.useState<OutputFileFormat>('CSV');

  const [selectedOutputForSubscription, setSelectedOutputForSubscription] = React.useState<(string | null)>(null);
  const [selectedRunFlowTaskId, setSelectedRunFlowTaskId] = React.useState<string | null>(null);

  const [emailRecipients, setEmailRecipients] = React.useState<Recipient[]>([]);

  React.useEffect(() => {
    Flows.getOutputStepInfos(props.flowId).then(items => {
     setOutputs(items);
    });

    const runFlowTasks = RunFlowTasks.all().addFilter(ServerFilterClauses.serverFilterClauses.flow(props.flowId));
    SliceableHelper.fetchAll(runFlowTasks).then(tasks => {
      setRunFlowTasks(tasks.items);
      setIsFetchingRunFlowTasks(false);
      });
  }, [props.flowId]);

  const isValidForm = (): boolean => {
    return isValidEmailTemplate(shouldSendToMe, emailRecipients, subjectValue, shouldIncludeOutputData, selectedOutputForSubscription)
      && selectedRunFlowTaskId !== null;
  };

  const handleRecipientsChange = (recipients: Recipient[]) => {
    setEmailRecipients(recipients);
  };

  const onOutputFileFormatChange = (value: OutputFileFormat) => {
    setOutputFileFormat(value);
  };

  const handleShouldSendToMeToggled = () => {
    setShouldSendToMe(!shouldSendToMe);
  };

  const handleShouldIncludeOutputDataToggled = () => {
    setShouldIncludeOutputData(!shouldIncludeOutputData);
  };

  const handleShouldShowFirstNRowsToggled = () => {
    setShouldShowFirstNRows(!shouldShowFirstNRows);
  };

  const handleShouldAttachOutputDataToEmailToggled = () => {
    setShouldAttachOutputDataToEmail(!shouldAttachOutputDataToEmail);
  };

  const handleShouldIncludeLinkToDatasourceToggled = () => {
    setShouldIncludeLinkToDatasource(!shouldIncludeLinkToDatasource);
  };

  const onSubjectChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSubjectValue(event.target.value);
  };

  const onMessageChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessageValue(event.target.value);
  };

  const onSelectedRunFlowTaskIdChange = (value: string) => {
    setSelectedRunFlowTaskId(value);

    // set outputs to only include outputs on the selected run flow task
    const selectedRunFlowTaskInfo: RunFlowTaskInfo = runFlowTasks.find((runFlowTask) => {
      return runFlowTask.id === value;
    });
    if (selectedRunFlowTaskInfo !== undefined && selectedRunFlowTaskInfo.flowOutputSteps && selectedRunFlowTaskInfo.flowOutputSteps.length > 0) {
      setOutputs(selectedRunFlowTaskInfo.flowOutputSteps);
      const selectedTaskDoesntHaveSelectedOutput: boolean = selectedRunFlowTaskInfo.flowOutputSteps.filter((output) => {
        return output.id === selectedOutputForSubscription;
      }).length < 1;
      if (selectedTaskDoesntHaveSelectedOutput) {
        setSelectedOutputForSubscription(null);
        setShouldIncludeOutputData(false);
      }
    } else {
      setSelectedOutputForSubscription(null);
      setShouldIncludeOutputData(false);
      setOutputs([]);
    }
  };

  const onSelectedOutputForSubscriptionChange = (value: string) => {
    if (value === selectedOutputForSubscription) {
      return;
    }
    const isOnPremEnvironment: boolean = !SessionServerSelectors.isOnlineEnvironment(getAppStore().getState());
    const isSelectedOutputPublishedDatasource: boolean = isSelectedOutputAPublishedDatasource(value, outputs);
    if (isOnPremEnvironment) {
      if (isSelectedOutputPublishedDatasource) {
        setShouldAttachOutputDataToEmail(false);
        setShouldIncludeLinkToDatasource(DEFAULT_SHOULD_INCLUDE_LINK_TO_DATASOURCE);
        setShouldShowFirstNRows(DEFAULT_SHOULD_SHOW_FIRST_N_ROWS);
      } else { // output that is not a published datasource
        setShouldIncludeLinkToDatasource(false);
        setShouldShowFirstNRows(DEFAULT_SHOULD_SHOW_FIRST_N_ROWS);
        setShouldAttachOutputDataToEmail(DEFAULT_SHOULD_ATTACH_OUTPUT_DATA_TO_EMAIL);
      }
    } else { // online
      setShouldIncludeLinkToDatasource(DEFAULT_SHOULD_INCLUDE_LINK_TO_DATASOURCE);
    }
    setSelectedOutputForSubscription(value);
  };

  const getSubscriptionFlow = (): SubscriptionFlow => {
    const runFlowTask: RunFlowTaskInfo = runFlowTasks.find((task) => {
      return task.id === selectedRunFlowTaskId;
    });
    const stepNumber: number | null = runFlowTask && runFlowTask.stepNumber ? runFlowTask.stepNumber : null;

    const currentUserId = SessionUserSelectors.sessionUserId(getAppStore().getState());
    const userIds = shouldSendToMe ?
      getEmailRecipientUserIds(emailRecipients, currentUserId)
      : getEmailRecipientUserIds(emailRecipients);

    const subscriptionFlowMetadata: SubscriptionFlowMetadata = {
      targetId: props.flowId,
      subject: subjectValue,
      message: messageValue,
      userIds: userIds,
    };

    const subscriptionFlowDetails: SubscriptionFlowDetails = {
      taskId: selectedRunFlowTaskId,
      groupIds: getEmailRecipientGroupIds(emailRecipients),
      includeOutputData: shouldIncludeOutputData,
      outputId: selectedOutputForSubscription,
      includeLinkToOutputData: shouldIncludeLinkToDatasource,
      showRowsInEmailBody: shouldShowFirstNRows,
      attachOutputData: shouldAttachOutputDataToEmail,
      attachedOutputDataFormat: outputFileFormat,
      stepNumber: stepNumber ? stepNumber.toString() : undefined,
    };

    const subscriptionFlow: SubscriptionFlow = {
      subscriptionFlowMetadata: subscriptionFlowMetadata,
      subscriptionFlowDetails: subscriptionFlowDetails,
    };

    return subscriptionFlow;
  };

  const showToastMessages = (result: IResult) => {
    if (result.errors && result.errors.length > 0) {
      showErrorMessages(result);
    } else {
      ToasterService.instance.pop({
        type: ToastType.Info,
        text: Messages.flowSubscriptionCreatedSuccess({FLOW_NAME: props.flowName}),
        testId: `tb-toast-flow-subscription-success`
      });
      props.dismiss();
    }
  };

  const showErrorMessages = (result: IResult) => {
    result.errors.forEach((error: IError) => {
      ToasterService.instance.pop({
        type: ToastType.Error,
        text: Messages.flowSubscriptionCreateFailed(),
        testId: `tb-toast-${error.code}`
      });
    });
  };

  const createFlowSubscription = () => {
    const selectedRunFlowTask = runFlowTasks.filter((runFlowTask) => {
      return runFlowTask.id === selectedRunFlowTaskId;
    })[0];

    setIsWaitingForCreate(true);
    Flows.createSubscriptionOnRunFlowTask(getSubscriptionFlow(), selectedRunFlowTask).then((result) => {
      showToastMessages(result);
      getAppStore().dispatch(requestSpaceContents());
    });
  };

  return (
    <>
      <div className={taskDropdownWrapper}>
        <FlowSubscriptionRunFlowTaskDropdown
          runFlowTasks={runFlowTasks}
          onChange={onSelectedRunFlowTaskIdChange}
          value={selectedRunFlowTaskId}
          isFetchingData={isFetchingRunFlowTasks}
          flowName={props.flowName}
        />
      </div>
      <FlowSubscriptionDialogContent
        hideInfoMessage={true}
        hideRecipientsAlert={true}
        outputs={outputs}
        shouldSendToMe={shouldSendToMe}
        subjectValue={subjectValue}
        messageValue={messageValue}
        shouldIncludeOutputData={shouldIncludeOutputData}
        selectedOutputForSubscription={selectedOutputForSubscription}
        shouldAttachOutputDataToEmail={shouldAttachOutputDataToEmail}
        shouldShowFirstNRows={shouldShowFirstNRows}
        outputFileFormat={outputFileFormat}
        shouldIncludeLinkToDatasource={shouldIncludeLinkToDatasource}
        emailRecipients={emailRecipients}
        onShouldSendToMeToggled={handleShouldSendToMeToggled}
        onShouldIncludeOutputDataToggled={handleShouldIncludeOutputDataToggled}
        onAttachOutputDataToEmailToggled={handleShouldAttachOutputDataToEmailToggled}
        onShowFirstNRowsToggled={handleShouldShowFirstNRowsToggled}
        onIncludeLinkToDatasourceToggled={handleShouldIncludeLinkToDatasourceToggled}
        onSubjectChange={onSubjectChange}
        onMessageChange={onMessageChange}
        onRecipientsChange={handleRecipientsChange}
        onOutputFileFormatChange={onOutputFileFormatChange}
        onSelectedOutputForSubscriptionChange={onSelectedOutputForSubscriptionChange}
      />
      <div className={buttonsWrapper}>
        <ButtonWidget
          testId={'cancel-create-flow-subscription-button'}
          buttonType={ ButtonType.Outline }
          handleClick={props.dismiss}
        >
          {Messages.flowSubscriptionsCancel()}
        </ButtonWidget>
        <ButtonWidget
          testId={'subscribe-to-flow-button-test-id'}
          buttonType={ ButtonType.Go }
          disabled={!isValidForm() || isWaitingForCreate}
          handleClick={() => { createFlowSubscription();} }
          style={{marginLeft: '12px'}}
        >
          {Messages.flowSubscriptionsSubscribe()}
        </ButtonWidget>
      </div>
    </>
  );
}